<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { BaseTable, BaseTableRow, BaseTableCell } from 'next/table';
import { messageStamp } from 'shared/helpers/timeHelper';
import TabBar from 'next/tabbar/TabBar.vue';
import Spinner from 'next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ReportHeader from '../reports/components/ReportHeader.vue';
import DashboardWidget from './pages/dashboards/components/DashboardWidget.vue';
import PeriodRangePicker from './pages/dashboards/components/PeriodRangePicker.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const reports = useMapGetter('wgptCustomReports/getCustomReports');
const reportsUiFlags = useMapGetter('wgptCustomReports/getUIFlags');
const dashboards = useMapGetter('wgptCustomDashboards/getCustomDashboards');
const dashboardsUiFlags = useMapGetter('wgptCustomDashboards/getUIFlags');

const TAB_KEYS = ['dashboards', 'reports'];

const activeTabKey = computed(() => {
  const candidate = String(route.query.tab || '');
  return TAB_KEYS.includes(candidate) ? candidate : 'dashboards';
});

const activeTabIndex = computed(() =>
  Math.max(0, TAB_KEYS.indexOf(activeTabKey.value))
);

const tabBarTabs = computed(() => [
  {
    key: 'dashboards',
    label: t('WGPT_CUSTOM_REPORTS.VIEWER_TABS.DASHBOARDS'),
  },
  { key: 'reports', label: t('WGPT_CUSTOM_REPORTS.VIEWER_TABS.REPORTS') },
]);

const selectedDashboardId = computed(() => {
  const raw = route.query.dashboardId;
  return raw ? Number(raw) : null;
});

const selectedDashboard = computed(() => {
  if (!selectedDashboardId.value) return null;
  return dashboards.value.find(d => d.id === selectedDashboardId.value) || null;
});

const dashboardOptions = computed(() => [
  {
    value: '',
    label: t('WGPT_CUSTOM_REPORTS.DASHBOARD_PICKER.PLACEHOLDER'),
  },
  ...dashboards.value.map(d => ({ value: d.id, label: d.name })),
]);

const tableHeaders = computed(() => [
  t('WGPT_CUSTOM_REPORTS.LIST.TABLE_HEADER.NAME'),
  t('WGPT_CUSTOM_REPORTS.LIST.TABLE_HEADER.EVENT'),
  t('WGPT_CUSTOM_REPORTS.LIST.UPDATED_ON'),
]);

const widgetGridClass = widget =>
  widget.width === 'full' ? 'col-span-1 md:col-span-2' : 'col-span-1';

// Dashboard-wide period filter applied to all widgets at runtime.
// Seeded by PeriodRangePicker's mount-time emit — seeding a second range here
// would differ by milliseconds and make every widget fetch twice.
const dashboardPeriod = ref(null);

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');

function updateQuery(patch) {
  const next = { ...route.query };
  Object.entries(patch).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      delete next[key];
    } else {
      next[key] = String(value);
    }
  });
  router.replace({ name: route.name, params: route.params, query: next });
}

function onTabChange(tab) {
  if (tab.key === activeTabKey.value) return;
  updateQuery({ tab: tab.key });
}

function onSelectDashboard(value) {
  updateQuery({ dashboardId: value || null });
}

function navigateToReport(report) {
  router.push(
    accountScopedRoute('wgpt_reports_custom_show', { reportId: report.id })
  );
}

onMounted(() => {
  store.dispatch('wgptCustomReports/get');
  store.dispatch('wgptCustomDashboards/get');
});

// Default dashboardId to the first one when entering Dashboards tab with none picked
watch(
  [activeTabKey, dashboards, selectedDashboardId],
  ([tabKey, list, currentId]) => {
    if (tabKey !== 'dashboards') return;
    if (currentId) {
      const stillExists = list.some(d => d.id === currentId);
      if (!stillExists && list.length) {
        updateQuery({ dashboardId: list[0].id });
      }
      return;
    }
    if (list.length) {
      updateQuery({ dashboardId: list[0].id });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <ReportHeader :header-title="t('WGPT_CUSTOM_REPORTS.HEADER')" />

    <div class="mb-6">
      <TabBar
        :tabs="tabBarTabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChange"
      />
    </div>

    <section v-if="activeTabKey === 'dashboards'">
      <div
        v-if="dashboardsUiFlags.isFetching && !dashboards.length"
        class="flex justify-center py-16"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!dashboards.length"
        class="flex flex-col items-center justify-center text-center py-20"
      >
        <div
          class="flex items-center justify-center size-14 rounded-full bg-n-alpha-2 mb-4"
        >
          <Icon
            icon="i-lucide-layout-dashboard"
            class="size-7 text-n-slate-10"
          />
        </div>
        <h3 class="text-base font-medium text-n-slate-12 mb-1">
          {{ t('WGPT_CUSTOM_REPORTS.DASHBOARD_PICKER.EMPTY_STATE_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 max-w-sm">
          {{
            t('WGPT_CUSTOM_REPORTS.DASHBOARD_PICKER.EMPTY_STATE_DESCRIPTION')
          }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-4">
        <div
          class="flex flex-col gap-1 [&_select]:w-full [&>div:last-child]:w-full sm:max-w-sm"
        >
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.DASHBOARD_PICKER.LABEL') }}
          </label>
          <Select
            :model-value="selectedDashboardId || ''"
            :options="dashboardOptions"
            @update:model-value="onSelectDashboard"
          />
        </div>

        <div
          v-if="!selectedDashboard"
          class="py-12 text-center text-sm text-n-slate-11"
        >
          {{ t('WGPT_CUSTOM_REPORTS.DASHBOARD_PICKER.PICK_PROMPT') }}
        </div>

        <div v-else class="flex flex-col gap-3">
          <p
            v-if="selectedDashboard.description"
            class="text-sm text-n-slate-11"
          >
            {{ selectedDashboard.description }}
          </p>

          <div
            v-if="!(selectedDashboard.widgets || []).length"
            class="py-12 text-center text-sm text-n-slate-11"
          >
            {{ t('WGPT_CUSTOM_DASHBOARDS.VIEWER.NO_WIDGETS') }}
          </div>

          <template v-else>
            <div class="flex flex-wrap items-center gap-3">
              <PeriodRangePicker @change="dashboardPeriod = $event" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="widget in selectedDashboard.widgets"
                :key="widget.id"
                :class="widgetGridClass(widget)"
              >
                <DashboardWidget
                  :widget="widget"
                  :period="dashboardPeriod"
                  drillable
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </section>

    <section v-else>
      <woot-loading-state v-if="reportsUiFlags.isFetching && !reports.length" />

      <p
        v-else-if="!reports.length"
        class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
      >
        {{ t('WGPT_CUSTOM_REPORTS.EMPTY_STATE.TITLE') }}
      </p>

      <BaseTable v-else :headers="tableHeaders" :items="reports">
        <template #row="{ items }">
          <BaseTableRow
            v-for="report in items"
            :key="report.id"
            :item="report"
            class="cursor-pointer hover:bg-n-alpha-1"
            @click="navigateToReport(report)"
          >
            <template #default>
              <BaseTableCell class="max-w-0 w-full">
                <div class="flex flex-col items-start gap-1">
                  <span class="block text-heading-3 text-n-slate-12">
                    {{ report.name }}
                  </span>
                  <p
                    v-if="report.description"
                    class="mb-0 text-n-slate-11 text-body-main truncate max-w-xs"
                  >
                    {{ report.description }}
                  </p>
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11 whitespace-nowrap">
                  {{ report.event_definition_name }}
                </span>
              </BaseTableCell>

              <BaseTableCell :title="readableDateWithTime(report.updated_at)">
                <span class="text-body-main text-n-slate-12 whitespace-nowrap">
                  {{ readableDate(report.updated_at) }}
                </span>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </section>
  </div>
</template>
